import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SelectorDeIdioma, useIdioma } from '../idioma';
import { supabase } from '../supabase';
import { useVerificacion } from '../componentes/verificacion';
import { errorDeClave } from './contrasena';

/** Lo que se pide al registrarse; viaja hasta el primer ingreso (donde se guarda y va al CRM). */
export type PerfilDeRegistro = { nombre: string; telefono: string; pais: string; negocio: string; marketing: boolean };
const CLAVE = 'igor-perfil';
export const perfilPendiente = (): PerfilDeRegistro | null => {
  try { const v = localStorage.getItem(CLAVE); return v ? (JSON.parse(v) as PerfilDeRegistro) : null; } catch { return null; }
};
export const olvidarPerfilPendiente = () => { try { localStorage.removeItem(CLAVE); } catch { /* nada */ } };

/** Entrar con Google (30-09-2026): un clic, sin contraseñas. */
export async function entrarConGoogle() {
  await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${window.location.origin}/marcas` } });
}

export function BotonGoogle({ texto }: { texto: string }) {
  return (
    <button type="button" className="btn claro" style={{ display: 'inline-flex', gap: 10, alignItems: 'center', justifyContent: 'center' }} onClick={() => void entrarConGoogle()}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {texto}
    </button>
  );
}

/** Crear la cuenta: los datos básicos y un enlace al correo (o Google). La prueba gratis empieza al entrar. */
export function Registro() {
  const { t } = useIdioma();
  const [f, setF] = useState<PerfilDeRegistro & { correo: string }>({ nombre: '', correo: '', telefono: '', pais: '', negocio: '', marketing: false });
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const verificacion = useVerificacion();
  const campo = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: k === 'marketing' ? e.target.checked : e.target.value });
  const guardar = () => { try { const { correo: _c, ...perfil } = f; localStorage.setItem(CLAVE, JSON.stringify(perfil)); } catch { /* nada */ } };
  const listo = f.nombre.trim() && f.correo.includes('@') && f.telefono.trim().length >= 7;

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setEnviando(true);
    guardar();
    const { error: fallo } = await supabase.auth.signInWithOtp({ email: f.correo.trim(), options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}/marcas`, captchaToken: verificacion.token } });
    verificacion.usado();
    setEnviando(false);
    if (fallo) return setError(t(/captcha/i.test(fallo.message) ? errorDeClave(fallo.message) : 'No pudimos enviarte el enlace. Revisa el correo y vuelve a intentarlo.'));
    setEnviado(true);
  }

  return (
    <main className="portada">
      <div className="idioma-flotante"><SelectorDeIdioma /></div>
      <div className="portada-caja tarjeta">
        {!enviado ? (
          <>
            <h1 className="titulo-portada">{t('Crea tu cuenta')}</h1>
            <p className="apunte">{t('Empiezas con créditos gratis para armar tu marca y tu mes. Sin tarjeta.')}</p>
            <form onSubmit={enviar} className="formulario">
              <label className="campo"><span>{t('Tu nombre')}</span><input required autoFocus value={f.nombre} onChange={campo('nombre')} /></label>
              <label className="campo"><span>{t('Tu correo')}</span><input type="email" required value={f.correo} onChange={campo('correo')} placeholder={t('tunombre@correo.com')} /></label>
              <label className="campo"><span>{t('Tu WhatsApp')}</span><input type="tel" required value={f.telefono} onChange={campo('telefono')} placeholder="+57 300 000 0000" /></label>
              <label className="campo"><span>{t('País')}</span><input value={f.pais} onChange={campo('pais')} placeholder={t('Colombia')} /></label>
              <label className="campo"><span>{t('Tu negocio')}<em className="apunte"> · {t('opcional')}</em></span><input value={f.negocio} onChange={campo('negocio')} /></label>
              <label className="casilla"><input type="checkbox" checked={f.marketing} onChange={campo('marketing')} /> {t('Quiero recibir consejos y novedades de IGOR')}</label>
              <button className="btn" disabled={enviando || !verificacion.token || !listo}>{enviando ? t('Enviando…') : t('Crear mi cuenta')}</button>
            </form>
            <div className="col" style={{ gap: 10, marginTop: 14 }}>
              <span className="apunte" style={{ textAlign: 'center' }}>{t('o')}</span>
              <BotonGoogle texto={t('Crear mi cuenta con Google')} />
              <span className="apunte" style={{ textAlign: 'center' }}>{t('¿Ya tienes cuenta?')} <Link to="/entrar">{t('Entra aquí')}</Link></span>
            </div>
          </>
        ) : (
          <>
            <h1 className="titulo-portada">{t('¡Listo! Revisa tu correo')}</h1>
            <p className="apunte">{t('Toca el botón del mensaje para entrar. Ábrelo en este mismo dispositivo.')}</p>
          </>
        )}
        {!enviado && verificacion.cuadro}
        {verificacion.fallo && <p className="error">{t('No pudimos comprobar que eres una persona. Recarga la página y vuelve a intentarlo.')}</p>}
        {error && <p className="error">{error}</p>}
      </div>
    </main>
  );
}
